/*
  APGO Homepage — shared behavior for all apgo-home-* sections.

  One file, per-section opt-in via data attributes:
    [data-apgo-home-section]                 section root
    [data-apgo-reveal]                       scroll-in reveal (adds .is-revealed)
    [data-apgo-count][data-apgo-count-to]    animated counter (starts on reveal)
    [data-apgo-hero-video][data-src]         lazy background video, fades in when playing
                                             (data-src-mobile: smaller file for phones)
    [data-apgo-live-video]                   tap-to-play live clip, src injected near viewport
    [data-apgo-home-init~="carousel"]        banner carousel: dots, arrows, autoplay
    [data-apgo-home-init~="rail"]            product row: arrows + mouse drag-to-scroll

  Re-initializes on shopify:section:load so the Theme Editor stays live.
*/
(function () {
  'use strict';

  /* Run-once guard: the file is loaded from layout/theme.liquid, but this
     keeps a stray duplicate include from setting up a second set of
     observers and listeners. */
  if (window.__apgoHomeBooted) return;
  window.__apgoHomeBooted = true;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = !!(navigator.connection && navigator.connection.saveData);

  function onIntersect(el, options, callback) {
    if (!('IntersectionObserver' in window)) {
      callback(el);
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          callback(entry.target);
        }
      });
    }, options);
    observer.observe(el);
  }

  /* ── Reveal ── */
  function initReveal(root) {
    root.querySelectorAll('[data-apgo-reveal]').forEach(function (el) {
      if (el.dataset.apgoBound) return;
      el.dataset.apgoBound = '1';
      if (reducedMotion) {
        el.classList.add('is-revealed');
        return;
      }
      onIntersect(el, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }, function (target) {
        target.classList.add('is-revealed');
      });
    });
  }

  /* ── Counters ── */
  function formatCount(value) {
    return Math.round(value).toLocaleString('en-US');
  }

  function animateCount(el) {
    if (el.dataset.apgoCounted) return;
    var to = parseFloat(el.dataset.apgoCountTo || '0');
    if (reducedMotion || !to) {
      el.dataset.apgoCounted = '1';
      el.textContent = formatCount(to);
      return;
    }
    var duration = 1400;
    var start = null;
    function step(ts) {
      if (el.dataset.apgoCounted) return;
      if (start === null) start = ts;
      var progress = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = formatCount(to * eased);
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.dataset.apgoCounted = '1';
      }
    }
    requestAnimationFrame(step);
  }

  function initCounters(root) {
    root.querySelectorAll('[data-apgo-count]').forEach(function (el) {
      if (el.dataset.apgoBound) return;
      el.dataset.apgoBound = '1';
      onIntersect(el, { threshold: 0.4 }, animateCount);
    });
  }

  /* ── Hero background video ── */
  function initHeroVideo(root) {
    var video = root.querySelector('[data-apgo-hero-video]');
    if (!video || video.dataset.apgoBound) return;
    video.dataset.apgoBound = '1';

    if (reducedMotion || saveData || !video.dataset.src) return;

    var stage = video.closest('[data-apgo-video-stage]') || video.closest('.apgo-home-hero__stage');
    var src = video.dataset.src;
    if (video.dataset.srcMobile && window.matchMedia('(max-width: 749px)').matches) {
      src = video.dataset.srcMobile;
    }

    function load() {
      if (video.src) return;
      video.src = src;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.addEventListener('playing', function () {
        if (stage) stage.classList.add('is-playing');
      });
      var playPromise = video.play();
      if (playPromise && playPromise.catch) playPromise.catch(function () {});
    }

    if (document.readyState === 'complete') {
      setTimeout(load, 150);
    } else {
      window.addEventListener('load', function () {
        setTimeout(load, 150);
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!video.src) return;
          if (entry.isIntersecting) {
            var p = video.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.05 }).observe(video);
    }
  }

  /* ── Live proof videos ──
     Autoplay muted + looping while in view (the only autoplay browsers
     allow); sound stays off until the viewer taps the speaker button. */
  var isMobileViewport = window.matchMedia('(max-width: 749px)').matches;

  /* Autoplay downloads the whole clip. Desktop always autoplays; mobile only
     when the merchant enables it per section (off until the files are small
     enough to be kind to mobile data). */
  function liveAutoplayAllowed(card) {
    if (reducedMotion || saveData) return false;
    if (!isMobileViewport) return true;
    var section = card && card.closest ? card.closest('[data-apgo-live-autoplay-mobile]') : null;
    return !!section && section.dataset.apgoLiveAutoplayMobile === 'true';
  }

  function loadLiveSrc(video, preloadMode) {
    if (video.src || !video.dataset.src) return;
    /* 'metadata' when we're not going to autoplay: enough for a poster frame
       without pulling the whole clip down on mobile data. */
    video.preload = preloadMode || 'metadata';
    video.src = video.dataset.src + '#t=0.1';
    /* Chrome won't decode a poster frame from metadata alone — nudge a
       seek once metadata arrives so a frame paints even if autoplay is off. */
    video.addEventListener('loadedmetadata', function primeFrame() {
      video.removeEventListener('loadedmetadata', primeFrame);
      if (video.paused) {
        try {
          video.currentTime = 0.101;
        } catch (e) {}
      }
    });
  }

  function playLive(video) {
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }

  function setLiveSound(card, unmuted) {
    var video = card.querySelector('video');
    var button = card.querySelector('[data-apgo-live-sound]');
    if (!video) return;
    video.muted = !unmuted;
    card.classList.toggle('is-unmuted', unmuted);
    if (button) {
      button.setAttribute('aria-pressed', unmuted ? 'true' : 'false');
      button.setAttribute('aria-label', unmuted ? 'Turn sound off' : 'Turn sound on');
    }
  }

  /* Bound-node tracking uses a WeakSet, not a data attribute: the theme's
     DOM morphing clones attributes onto fresh nodes, so an attribute flag
     would mark a brand-new (unbound) node as already initialized. */
  var liveBound = typeof WeakSet === 'function' ? new WeakSet() : null;
  var liveObserver = null;

  function getLiveObserver() {
    if (liveObserver || !('IntersectionObserver' in window)) return liveObserver;
    /* One shared, persistent observer: play on enter, pause on leave, every time. */
    liveObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target.querySelector('video');
        if (!v) return;
        if (entry.isIntersecting) {
          var autoplay = liveAutoplayAllowed(entry.target);
          loadLiveSrc(v, autoplay ? 'auto' : 'metadata');
          if (autoplay) playLive(v);
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { threshold: 0.25 });
    return liveObserver;
  }

  function initLiveVideos(root) {
    root.querySelectorAll('[data-apgo-live-video]').forEach(function (card) {
      if (liveBound) {
        if (liveBound.has(card)) return;
        liveBound.add(card);
      } else if (card.dataset.apgoBound) {
        return;
      } else {
        card.dataset.apgoBound = '1';
      }

      var video = card.querySelector('video');
      if (!video) return;

      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      var observer = getLiveObserver();
      if (observer) {
        observer.observe(card);
      } else {
        var canAutoplay = liveAutoplayAllowed(card);
        loadLiveSrc(video, canAutoplay ? 'auto' : 'metadata');
        if (canAutoplay) playLive(video);
      }
    });
  }

  /* The speaker button's click is DELEGATED at document level: the theme's page
     transitions can morph #MainContent after init, which keeps attributes
     (apgoBound survives) but strips per-node listeners. Same defense
     apgo-collection-variant-drawer.js uses for its + button. The clips
     themselves are not clickable — they are ambient, muted loops. */
  if (!window.__apgoHomeLiveDelegated) {
    window.__apgoHomeLiveDelegated = true;

    /* Media events don't bubble, so these are capture-phase listeners on
       document — same reason as the delegated clicks, they survive DOM
       morphing that would strip per-element listeners. */
    document.addEventListener(
      'playing',
      function (e) {
        var card = e.target && e.target.closest ? e.target.closest('[data-apgo-live-video]') : null;
        if (card) card.classList.add('is-playing');
      },
      true
    );
    document.addEventListener(
      'pause',
      function (e) {
        var card = e.target && e.target.closest ? e.target.closest('[data-apgo-live-video]') : null;
        if (card) card.classList.remove('is-playing');
      },
      true
    );

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;

      var soundButton = e.target.closest('[data-apgo-live-sound]');
      if (soundButton) {
        e.preventDefault();
        var soundCard = soundButton.closest('[data-apgo-live-video]');
        var soundVideo = soundCard && soundCard.querySelector('video');
        if (!soundVideo) return;
        var unmute = soundVideo.muted;
        if (unmute) {
          /* Only one clip may play out loud at a time. */
          document.querySelectorAll('[data-apgo-live-video]').forEach(function (other) {
            if (other !== soundCard) setLiveSound(other, false);
          });
        }
        setLiveSound(soundCard, unmute);
        loadLiveSrc(soundVideo, 'auto');
        if (soundVideo.paused) playLive(soundVideo);
      }
    });
  }

  /* Bind-once guard shared by the carousel and product rows. A WeakSet, for
     the same DOM-morphing reason as the live videos above. */
  var sectionBound = typeof WeakSet === 'function' ? new WeakSet() : null;
  function bindOnce(el, key) {
    if (sectionBound) {
      if (sectionBound.has(el)) return false;
      sectionBound.add(el);
      return true;
    }
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  }

  /* ── Banner carousel ──
     Native scroll-snap track; dots and desktop arrows jump one banner,
     wrapping at the ends. Autoplay pauses on hover, focus, touch, when the
     tab is hidden or the carousel is off screen, and never runs with
     reduced motion. */
  function initCarousel(root) {
    if (!bindOnce(root, 'apgoCarouselBound')) return;
    var track = root.querySelector('[data-apgo-carousel-track]');
    var slides = root.querySelectorAll('[data-apgo-carousel-slide]');
    if (!track || slides.length < 2) return;

    var dots = root.querySelectorAll('[data-apgo-carousel-dot]');
    var prev = root.querySelector('[data-apgo-carousel-prev]');
    var next = root.querySelector('[data-apgo-carousel-next]');
    var count = slides.length;

    function current() {
      var width = track.clientWidth;
      return width ? Math.round(track.scrollLeft / width) : 0;
    }

    function goTo(index) {
      var target = ((index % count) + count) % count;
      track.scrollTo({ left: target * track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    function syncDots() {
      var active = current();
      Array.prototype.forEach.call(dots, function (dot, i) {
        var on = i === active;
        dot.classList.toggle('is-active', on);
        if (on) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    var scrollFrame = 0;
    track.addEventListener(
      'scroll',
      function () {
        if (scrollFrame) return;
        scrollFrame = requestAnimationFrame(function () {
          scrollFrame = 0;
          syncDots();
        });
      },
      { passive: true }
    );

    var autoplay = root.getAttribute('data-autoplay') === 'true' && !reducedMotion;
    var interval = Math.max(3000, parseInt(root.getAttribute('data-interval') || '5000', 10));
    var timer = null;
    var paused = false;
    var onScreen = true;

    function tick() {
      if (!paused && onScreen && !document.hidden) goTo(current() + 1);
    }
    function start() {
      if (autoplay && !timer) timer = setInterval(tick, interval);
    }
    function restart() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      start();
    }

    Array.prototype.forEach.call(dots, function (dot) {
      dot.addEventListener('click', function () {
        goTo(parseInt(dot.getAttribute('data-apgo-carousel-dot'), 10) || 0);
        restart();
      });
    });
    if (prev) {
      prev.addEventListener('click', function () {
        goTo(current() - 1);
        restart();
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        goTo(current() + 1);
        restart();
      });
    }

    root.addEventListener('pointerenter', function () { paused = true; });
    root.addEventListener('pointerleave', function () { paused = false; });
    root.addEventListener('focusin', function () { paused = true; });
    root.addEventListener('focusout', function () { paused = false; });
    track.addEventListener('touchstart', function () { paused = true; }, { passive: true });
    track.addEventListener(
      'touchend',
      function () {
        paused = false;
        restart();
      },
      { passive: true }
    );

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          onScreen = entries[0].isIntersecting;
        },
        { threshold: 0.3 }
      ).observe(root);
    }

    var resizeFrame = 0;
    window.addEventListener('resize', function () {
      var index = current();
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(function () {
        track.scrollTo({ left: index * track.clientWidth, behavior: 'auto' });
      });
    });

    start();
  }

  /* ── Product rows ──
     The arrows on the row's edges move by the number of whole cards in
     view and disable at either end; they hide when nothing overflows. Mouse
     users can also grab the row and drag it — same pattern as the clearance
     page: a drag longer than 6px swallows the click that follows. */
  function initRail(root) {
    if (!bindOnce(root, 'apgoRailBound')) return;
    var track = root.querySelector('[data-apgo-rail-track]');
    if (!track) return;
    var prev = root.querySelector('[data-apgo-rail-prev]');
    var next = root.querySelector('[data-apgo-rail-next]');

    function overflows() {
      return track.scrollWidth > track.clientWidth + 2;
    }

    function update() {
      var hasOverflow = overflows();
      var maxLeft = track.scrollWidth - track.clientWidth - 2;
      root.classList.add('is-rail-ready');
      root.classList.toggle('has-overflow', hasOverflow);
      if (prev) prev.disabled = !hasOverflow || track.scrollLeft <= 2;
      if (next) next.disabled = !hasOverflow || track.scrollLeft >= maxLeft;
    }

    function stepSize() {
      var slide = track.querySelector('.apgo-home-rail__slide');
      if (!slide) return track.clientWidth * 0.8;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      var width = slide.getBoundingClientRect().width + gap;
      var inView = Math.max(1, Math.floor((track.clientWidth + gap) / width + 0.01));
      return inView * width;
    }

    function move(direction) {
      track.scrollBy({ left: direction * stepSize(), behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { move(-1); });
    if (next) next.addEventListener('click', function () { move(1); });

    var frame = 0;
    track.addEventListener(
      'scroll',
      function () {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = 0;
          update();
        });
      },
      { passive: true }
    );
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(track);
    } else {
      window.addEventListener('resize', update);
    }
    update();

    /* Mouse drag-to-scroll */
    var pointerId = null;
    var startX = 0;
    var startScroll = 0;
    var moved = false;
    var swallowClick = false;

    track.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!overflows()) return;
      if (event.target.closest('button, input, select, textarea')) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      moved = false;
    });

    track.addEventListener('pointermove', function (event) {
      if (pointerId === null || event.pointerId !== pointerId) return;
      var distance = event.clientX - startX;
      if (!moved) {
        if (Math.abs(distance) < 6) return;
        moved = true;
        track.classList.add('is-dragging');
        try {
          track.setPointerCapture(pointerId);
        } catch (error) {}
      }
      track.scrollLeft = startScroll - distance;
    });

    function endDrag(event) {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;
      pointerId = null;
      if (!moved) return;
      track.classList.remove('is-dragging');
      swallowClick = true;
      window.setTimeout(function () {
        swallowClick = false;
      }, 0);
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', endDrag);
    track.addEventListener(
      'click',
      function (event) {
        if (!swallowClick) return;
        event.preventDefault();
        event.stopPropagation();
        swallowClick = false;
      },
      true
    );
  }

  /* ── Boot ── */
  var initializers = {
    reveal: initReveal,
    counters: initCounters,
    herovideo: initHeroVideo,
    livevideo: initLiveVideos,
    carousel: initCarousel,
    rail: initRail
  };

  function initSection(root) {
    var keys = (root.dataset.apgoHomeInit || '').split(/\s+/);
    keys.forEach(function (key) {
      if (initializers[key]) initializers[key](root);
    });
  }

  function initAll(scope) {
    document.documentElement.classList.add('apgo-home-js');
    (scope || document).querySelectorAll('[data-apgo-home-section]').forEach(initSection);
    scheduleFailsafe();
  }

  /* Failsafe: if IntersectionObserver / rAF never fire (broken embedder,
     ancient browser), force-reveal everything and finalize counters so the
     page is never left invisible. */
  var failsafeTimer = null;
  function scheduleFailsafe() {
    if (failsafeTimer) clearTimeout(failsafeTimer);
    failsafeTimer = setTimeout(function () {
      document.querySelectorAll('.apgo-home [data-apgo-reveal]:not(.is-revealed)').forEach(function (el) {
        el.classList.add('is-revealed');
      });
      document.querySelectorAll('.apgo-home [data-apgo-count]').forEach(function (el) {
        if (!el.dataset.apgoCounted) {
          el.dataset.apgoCounted = '1';
          el.textContent = formatCount(parseFloat(el.dataset.apgoCountTo || '0'));
        }
      });
    }, 2200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  /* Self-heal after the theme's view transitions swap #MainContent: the
     replacement nodes are new objects, so initAll re-observes them (the
     WeakSet guard keeps already-live cards from being bound twice). */
  if (typeof MutationObserver === 'function' && !window.__apgoHomeMutationBound) {
    window.__apgoHomeMutationBound = true;
    var reinitTimer = null;
    new MutationObserver(function () {
      if (reinitTimer) clearTimeout(reinitTimer);
      reinitTimer = setTimeout(function () {
        initAll(document);
      }, 120);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
